export type XmlSlashEdit = {
    source: string;
    startOffset: number;
    endOffset: number;
    text: string;
};

export type XmlSlashEditContext = {
    textUntilCursor: string;
    textAfterCursorOnLine: string;
    currentLineContent: string;
    cursorOffset: number;
    lineStartOffset: number;
    indentUnit?: string;
};

const XML_TAG_NAME_PATTERN: string = '[_:A-Za-z][_:A-Za-z.\\-\\d]*';
const OPENING_TAG_AT_CURSOR_PATTERN: RegExp = new RegExp(`<(${XML_TAG_NAME_PATTERN})(?:\\s+[^>]*)?$`);
const XML_TAG_PATTERN: RegExp = new RegExp(`</?(${XML_TAG_NAME_PATTERN})(?:\\s+[^>]*)?>`, 'g');
const XML_COMMENT_PATTERN: RegExp = /<!--[\s\S]*?-->/g;
const SELF_CLOSING_TAG_PATTERN: RegExp = /\/\s*>$/;

const getUnclosedElementNames = (xml: string): string[] => {
    const elementStack: string[] = [];

    for (const element of xml.matchAll(XML_TAG_PATTERN)) {
        const tagText: string = element[0].trimStart();
        const elementName: string | undefined = element[1];
        if (!elementName) continue;

        if (tagText.startsWith('</')) {
            if (elementStack.at(-1) === elementName) elementStack.pop();
            continue;
        }

        if (!SELF_CLOSING_TAG_PATTERN.test(tagText)) elementStack.push(elementName);
    }

    return elementStack;
};

export const getXmlSlashEdit = ({
    textUntilCursor,
    textAfterCursorOnLine,
    currentLineContent,
    cursorOffset,
    lineStartOffset,
    indentUnit = '   '
}: XmlSlashEditContext): XmlSlashEdit | undefined => {
    const textWithoutComments: string = textUntilCursor.replace(XML_COMMENT_PATTERN, '');
    const openingTagMatch: RegExpMatchArray | null = textWithoutComments.match(OPENING_TAG_AT_CURSOR_PATTERN);
    const isStartingClosingTag: boolean = textWithoutComments.endsWith('<');
    const unclosedElementNames: string[] = getUnclosedElementNames(textWithoutComments);

    if (openingTagMatch && !isStartingClosingTag) {
        const elementName: string = openingTagMatch[1];
        const closingTag: string = `></${elementName}>`;

        if (textAfterCursorOnLine === closingTag) {
            return {
                source: 'xml-self-closing-tag',
                startOffset: cursorOffset,
                endOffset: cursorOffset + closingTag.length,
                text: '/>'
            };
        }

        if (textAfterCursorOnLine.length === 0) {
            return {
                source: 'xml-self-closing-tag',
                startOffset: cursorOffset,
                endOffset: cursorOffset,
                text: '/>'
            };
        }
    }

    if (!isStartingClosingTag || !unclosedElementNames.length) return undefined;

    const lastElementName: string = unclosedElementNames[unclosedElementNames.length - 1];
    const indentationMatch: RegExpMatchArray | null = currentLineContent.match(/^(\s*)<$/);

    if (indentationMatch) {
        return {
            source: 'xml-closing-tag',
            startOffset: lineStartOffset,
            endOffset: cursorOffset,
            text: `${indentUnit.repeat(unclosedElementNames.length - 1)}</${lastElementName}>`
        };
    }

    return {
        source: 'xml-closing-tag',
        startOffset: cursorOffset,
        endOffset: cursorOffset,
        text: `/${lastElementName}>`
    };
};
